import React from 'react';
import { LockClosedIcon } from '@heroicons/react/16/solid';
import { classNames } from '../utils/classNames';

// A row of tabs that selects one option inside the page, with the look of PageButtonTabs. PageButtonTabs opens
// another page (a router link); ButtonTabs only reports the choice. The caller owns the state: onChange receives
// the next value. Below the sm width the row becomes a select, as the page tabs do.
// An option can carry an icon before its label and a short note (for example "your market"): the note is the
// title of the tab, the screen reader reads it after the label, and the select shows it in brackets.
// One option can be disabled while the others stay active (for example a measure that needs data the hotel
// has not given yet): it shows a lock, it cannot be selected, and its note says why.
export type ButtonTabsOption = {
	value: string,
	label: string,
	icon?: React.ReactNode,
	note?: string,
	disabled?: boolean,
};

type Props = {
	// The accessible name of the group ("Markets of the Athens region").
	label: string,
	options: ButtonTabsOption[],
	value: string,
	onChange: (value: string) => void,
	disabled?: boolean,
};

const ButtonTabs: React.FunctionComponent<Props> = ({ label, options, value, onChange, disabled = false }) => {
	return <div>
		<div className="sm:hidden">
			<select aria-label={label} value={value} disabled={disabled}
				onChange={event => onChange(event.target.value)}
				className={classNames('w-full input', disabled ? 'input-disabled' : '')}>
				{options.map(option => <option key={option.value} value={option.value} disabled={option.disabled === true}>
					{option.note !== undefined ? `${option.label} (${option.note})` : option.label}
				</option>)}
			</select>
		</div>
		<div role="group" aria-label={label} className="hidden sm:flex sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-2">
			{options.map(option => {
				const selected = option.value === value;
				const off = disabled || option.disabled === true;
				return <button key={option.value} type="button" aria-pressed={selected} title={option.note} disabled={off}
					onClick={() => { if (!selected) onChange(option.value); }}
					className={classNames(
						'inline-flex items-center gap-x-1.5 rounded-md px-3 py-1.5 text-sm font-medium focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
						selected ? 'bg-blue-100 text-blue-700' : 'text-gray-500',
						off ? 'cursor-not-allowed opacity-50' : (selected ? '' : 'hover:text-gray-700'),
					)}>
					{option.icon !== undefined && <span aria-hidden="true" className="inline-flex shrink-0">{option.icon}</span>}
					{option.label}
					{option.disabled === true && <LockClosedIcon aria-hidden="true" className="h-3 w-3 shrink-0" />}
					{option.note !== undefined && <span className="sr-only"> ({option.note})</span>}
				</button>;
			})}
		</div>
	</div>;
};

export default ButtonTabs;
